import jQuery from "jquery";

window.$ = jQuery; // workaround for https://github.com/parcel-bundler/parcel/issues/333

import "bootstrap";

import instantsearch from "instantsearch.js/es";
import {
  searchBox,
  infiniteHits,
  configure,
  stats,
  refinementList,
  numericMenu,
  sortBy,
  currentRefinements,
} from "instantsearch.js/es/widgets";
import { history } from "instantsearch.js/es/lib/routers";

import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";
import STOP_WORDS from "./utils/stop_words.json";
import I18N from "./i18n.json";

// ---------------------------------------------------------------------------
// i18n
// ---------------------------------------------------------------------------
function detectLang() {
  const urlLang = new URLSearchParams(window.location.search).get("lang");
  if (urlLang && I18N[urlLang]) return urlLang;
  const browserLang = (navigator.language || "de").split("-")[0];
  return I18N[browserLang] ? browserLang : "de";
}

const lang = detectLang();
// Zahlen- und Preisform (Auftrag C8): derselbe Kern wie window.vlFormat im Shop-Theme (layout/meta.html.twig), hier eingebettet,
// weil der Deploy (TypeSense §6.3) nur app.js uebertraegt.
/* vlFormat-Kern: Anfang. Regeln = Service/Formatierer.php des Themes (Auftrag C8); derselbe Kern steht byte-gleich in
   layout/meta.html.twig (window.vlFormat) und in der Such-SPA src/app.js, pruefung/c8_formatierer_test.js prueft beides.
   Feste Regeln statt Intl.NumberFormat: Browser-ICU schreibt je nach Stand U+00A0 oder US$ (E13). */
function vlFormatKern(sprache) {
  var en = String(sprache || '').toLowerCase().indexOf('en') === 0;
  var SYMBOLE = ['€', '$', '£'];
  var ZEICHEN = { EUR: '€', USD: '$', 'US$': '$', GBP: '£' };
  var ZAHL = /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/;
  function plusEins(s) {
    var a = s.split(''), i = a.length - 1;
    while (i >= 0 && a[i] === '9') { a[i] = '0'; i--; }
    if (i < 0) { return '1' + a.join(''); }
    a[i] = String(Number(a[i]) + 1);
    return a.join('');
  }
  function teile(n, stellen) {
    if (typeof n === 'string') {
      n = n.trim();
      if (!ZAHL.test(n)) { return null; }
    } else if (typeof n !== 'number') {
      return null;
    }
    var f = Number(n);
    if (!isFinite(f)) { return null; }
    stellen = Math.max(0, stellen | 0);
    var e = Math.abs(f).toExponential(14).split('e');
    var ziffern = e[0].replace('.', '');
    var punkt = parseInt(e[1], 10) + 1;
    if (punkt <= 0) {
      ziffern = new Array(2 - punkt).join('0') + ziffern;
      punkt = 1;
    } else if (punkt > ziffern.length) {
      ziffern += new Array(punkt - ziffern.length + 1).join('0');
    }
    var ganz = ziffern.slice(0, punkt);
    var bruch = ziffern.slice(punkt);
    while (bruch.length < stellen + 1) { bruch += '0'; }
    var kopf = ganz + bruch.slice(0, stellen);
    if (Number(bruch.charAt(stellen)) >= 5) { kopf = plusEins(kopf); }
    ganz = kopf.slice(0, kopf.length - stellen).replace(/^0+/, '') || '0';
    bruch = stellen > 0 ? kopf.slice(kopf.length - stellen) : '';
    var minus = f < 0 && (ganz + bruch).replace(/0/g, '') !== '';
    return [minus, ganz, bruch];
  }
  function zeichen(waehrung) {
    var w = String(waehrung == null ? '' : waehrung).trim();
    if (w === '') { return '€'; }
    return Object.prototype.hasOwnProperty.call(ZEICHEN, w.toUpperCase()) ? ZEICHEN[w.toUpperCase()] : w;
  }
  function zahl(n, stellen) {
    stellen = stellen === undefined ? 0 : stellen;
    var t = teile(n, stellen);
    if (t === null) { return ''; }
    var tsd = en ? ',' : '.', dez = en ? '.' : ',';
    var ganz = t[1].replace(/\B(?=(\d{3})+(?!\d))/g, tsd);
    return (t[0] ? '-' : '') + ganz + (stellen > 0 ? dez + t[2] : '');
  }
  function preis(betrag, waehrung, trenner, stellen) {
    trenner = trenner === undefined ? ' ' : trenner;
    var z = zahl(betrag, stellen === undefined ? 2 : stellen);
    if (z === '') { return ''; }
    var s = zeichen(waehrung);
    if (!en) { return z + trenner + s; }
    var minus = z.charAt(0) === '-';
    return (minus ? '-' : '') + s + (SYMBOLE.indexOf(s) >= 0 ? '' : trenner) + (minus ? z.slice(1) : z);
  }
  return { sprache: en ? 'en' : 'de', zeichen: zeichen, zahl: zahl, preis: preis };
}
/* vlFormat-Kern: Ende */
// Sprache nur aus dem vom Shop gesetzten `lang`, nie aus der Browsersprache (E14) — ohne Parameter Deutsch.
const FORMAT = vlFormatKern(new URLSearchParams(window.location.search).get("lang") === "en" ? "en" : "de");
function t(key) {
  return (I18N[lang] && I18N[lang][key]) || (I18N["de"] && I18N["de"][key]) || key;
}

// Grade-Labels für abbr-Tooltips auf Zustandscodes
const GRADE_LABELS = {
  "M":   "Mint",
  "NM":  "Near Mint",
  "NM-": "Near Mint minus",
  "EX":  "Excellent",
  "VG+": "Very Good Plus",
  "VG":  "Very Good",
  "VG-": "Very Good minus",
  "G+":  "Good Plus",
  "G":   "Good",
  "F":   "Fair",
  "P":   "Poor",
};

// ---------------------------------------------------------------------------
// Typesense server config
// ---------------------------------------------------------------------------
const anchorParams = new Proxy(
  new URLSearchParams(window.location.hash.replace("#", "")),
  { get: (searchParams, prop) => searchParams.get(prop) }
);

let TYPESENSE_SERVER_CONFIG = {
  apiKey: process.env.TYPESENSE_SEARCH_ONLY_API_KEY,
  nodes: [
    {
      host: anchorParams.host ? anchorParams.host : process.env.TYPESENSE_HOST,
      port: process.env.TYPESENSE_PORT,
      protocol: process.env.TYPESENSE_PROTOCOL,
      path: process.env.TYPESENSE_PATH || "/api",
    },
  ],
  numRetries: 8,
  useServerSideSearchCache: true,
};

if (process.env[`TYPESENSE_HOST_2`]) {
  TYPESENSE_SERVER_CONFIG.nodes.push({
    host: anchorParams.host ? anchorParams.host : process.env[`TYPESENSE_HOST_2`],
    port: process.env.TYPESENSE_PORT,
    protocol: process.env.TYPESENSE_PROTOCOL,
    path: process.env.TYPESENSE_PATH || "/api",
  });
}

if (process.env[`TYPESENSE_HOST_3`]) {
  TYPESENSE_SERVER_CONFIG.nodes.push({
    host: anchorParams.host ? anchorParams.host : process.env[`TYPESENSE_HOST_3`],
    port: process.env.TYPESENSE_PORT,
    protocol: process.env.TYPESENSE_PROTOCOL,
    path: process.env.TYPESENSE_PATH || "/api",
  });
}

if (process.env[`TYPESENSE_HOST_NEAREST`]) {
  TYPESENSE_SERVER_CONFIG.nearestNode = {
    host: anchorParams.host ? anchorParams.host : process.env[`TYPESENSE_HOST_NEAREST`],
    port: process.env.TYPESENSE_PORT,
    protocol: process.env.TYPESENSE_PROTOCOL,
    path: process.env.TYPESENSE_PATH || "/api",
  };
}

// ---------------------------------------------------------------------------
// Dynamic runtime params – passed by Shopware via iframe URL
// collection: maps to TypeSense collection for this sales channel
// shopwareOrigin: the Shopware storefront origin for postMessage (replaces .env.production)
// ---------------------------------------------------------------------------
const _runtimeParams = new URLSearchParams(window.location.search);

// salesChannelId → TypeSense collection mapping
// Keyed by Shopware sales channel ID (lowercase hex, no dashes)
// IDs queried from Shopware API 2026-02-22
const _CHANNEL_COLLECTION_MAP = {
  '0192c2d81410726d91e427fff08483af': 'products_gebrauchte_cds',       // Gebrauchte-CDs-kaufen
  '01954d1789bd7d699d90ead56a591601': 'products_schallplatten',         // Schallplatten
  '019928d2d19f7e5cadf261f1f00be559': 'products_schellack',             // Schellack
  '98432def39fc4624b33213a56b8c944d': 'products_vinyliebhaberei_ebay',  // Vinyliebhaberei - Ebay
  '019928c18ba7791082e68ff526499266': 'products_vinyl',                 // Vinyl
};

const _salesChannelId = (_runtimeParams.get('salesChannelId') || '').toLowerCase();

// Waehrung (Versandkosten 8k-2b Schritt D, 25.09.2026): der Shop haengt ?waehrung=<ISO> an die
// iframe-URL (Kontextwaehrung des Gastes). Nur Waehrungen mit eigenem Preisfeld im Index schalten um,
// alles andere bleibt Euro. preis_chf = der CHF-Betrag aus product.price (inkl. Versand, R035), also
// genau der Betrag, den der Shop in Franken verlangt.
const _PREIS_FELD_JE_WAEHRUNG = { CHF: 'preis_chf' };
const _waehrung = (_runtimeParams.get('waehrung') || '').toUpperCase();
const PREIS_FELD = _PREIS_FELD_JE_WAEHRUNG[_waehrung] || 'preis';
const WAEHRUNG_ZEICHEN = PREIS_FELD === 'preis' ? 'EUR' : _waehrung;
// Preisbaender (Auftrag C8, E9): Wortlaut aus i18n.json mit Platzhaltern wie die Theme-Snippets vinyl.list.band*, Betraege aus
// FORMAT (de "Bis 5 €", "5 – 15 €"; en "Up to €5", "€5 – €15"; Franken "CHF 5"). Gleiche Grenzen in jeder Waehrung
// (Betreiber 25.09.2026), nur das Zeichen wechselt. Grenzen wie FormatExtension::PREISBAENDER im Theme.
const PREISBAENDER = [[null, 5], [5, 15], [15, 30], [30, 50], [50, 100], [100, null]];
function bandLabel(von, bis) {
  const p = (b) => FORMAT.preis(b, WAEHRUNG_ZEICHEN, " ", 0);
  if (von === null) return t("filterBandUpTo").replace("{bis}", p(bis));
  if (bis === null) return t("filterBandOver").replace("{von}", p(von));
  return t("filterBandRange").replace("{vonZahl}", FORMAT.zahl(von)).replace("{von}", p(von)).replace("{bis}", p(bis));
}
// Sichtbarkeit geteilter Maerkte (V1/B4, 17.10.2026, TypeSense ARCHITEKTUR §6): teilt ein Markt die Collection eines
// anderen (starvinylworld.com in products_schallplatten), zeigt die SPA nur Artikel mit <feld>:=true.
// Wahrheit = Register (typesense_sichtbarkeit), der Resolver reicht es als ?sichtbarkeit= durch. Die Tabelle ist nur der
// Rueckfall fuer eine iframe-Adresse ohne Parameter (alter HTTP-Cache): Ableitung des Registers, bei jedem neuen
// geteilten Markt von Hand mitziehen. Ohne Treffer: kein Zusatz (eu, Altkanaele) = Filter wie bisher.
const _SICHTBARKEIT_JE_KANAL = {
  '01a148ae1b3f713fb0307c4979f4339a': 'visible_starvinylworld',  // Starvinylworld (Markt us)
};
const _sichtbarkeitParam = _runtimeParams.get('sichtbarkeit') || '';
const SICHTBARKEIT_FELD = /^visible_[a-z0-9_]+$/.test(_sichtbarkeitParam)
  ? _sichtbarkeitParam
  : (_SICHTBARKEIT_JE_KANAL[_salesChannelId] || '');
const BASIS_FILTER = "visible_any:=true && missing_product:=false"
  + (SICHTBARKEIT_FELD ? ` && ${SICHTBARKEIT_FELD}:=true` : "");

const INDEX_NAME = _runtimeParams.get('collection')
  || _CHANNEL_COLLECTION_MAP[_salesChannelId]
  || process.env.TYPESENSE_COLLECTION_NAME;

// shopwareOrigin: URL-param (from PHP resolver) → document.referrer → env-var
const _shopwareOrigin = _runtimeParams.get('shopwareOrigin')
  || (document.referrer ? new URL(document.referrer).origin : null)
  || process.env.SHOPWARE_TARGET_ORIGIN
  || null;

// ---------------------------------------------------------------------------
// Produktbilder (P1, 05.09.2026)
// ---------------------------------------------------------------------------
// Der Suchindex fuehrt die Bildadressen seit dem Bildgewicht-Umbau RELATIV
// ('/media/...', '/thumbnail/...'), damit der Shop sie von sich selbst laedt.
// Diese SPA laeuft aber auf einem ANDEREN Host (musicsearch.axel-haase.de) —
// ein relativer Pfad wuerde hier gegen den SPA-Host aufloesen und 404en.
// Deshalb wird die Shop-Herkunft davorgesetzt: bevorzugt die, aus der die SPA
// eingebettet ist (_shopwareOrigin, kommt per iframe-Parameter bzw. Referrer),
// sonst der Shop selbst — dieselbe Kette wie bei den /artist/-Links unten.
//
// Zusaetzlich werden die skalierten Stufen genutzt statt der Originale: gemessen
// 338 KB Original gegen 16 KB (280er) / 31 KB (400er) bei ~220 px Kachelbreite.
// Die Kachel hat KEIN Zoom-CSS (der transform-origin-Handler unten ist folgenlos),
// deshalb reicht die 400er-Stufe hier vollstaendig aus.
const SHOP_BILD_BASIS = _shopwareOrigin || "https://vinyliebhaberei.de";

function bildUrl(pfad) {
  const p = (pfad || "").trim();
  if (!p) return "";
  if (/^(https?:)?\/\//i.test(p)) return p;   // Altdokument mit absoluter Adresse
  return SHOP_BILD_BASIS + (p.charAt(0) === "/" ? p : "/" + p);
}

// Liefert { src, srcset } fuer eine Trefferkachel. Ohne Thumbnails faellt es auf
// das Original zurueck — lieber schwer als gar kein Bild.
function bildQuellen(hit) {
  const stufen = [];
  [280, 400].forEach((w) => {
    const u = bildUrl(hit["thumb_" + w]);
    if (u) stufen.push({ w, u });
  });
  if (!stufen.length) return { src: bildUrl(hit.image_path), srcset: "" };
  return {
    src: stufen[0].u,
    srcset: stufen.map((s) => s.u + " " + s.w + "w").join(", "),
  };
}

// ---------------------------------------------------------------------------
// Format-Label-Mapping (format_group-Token → Anzeige), i18n via i18n.json.
// Spiegel von FormatVocabulary::GROUP_TO_LABEL im Theme-Plugin (Single Source of
// Truth). Seit den distinkten Tokens (v1.2.0) kollektion-UNABHÄNGIG — der frühere
// isCdCollection-Trick für vinyl_maxi_12 ist obsolet (die CD-Collection trägt jetzt
// eigene cd/cd_maxi/cd_box-Tokens). Sprache aus `lang`; de bildet die Basis, die
// aktive Sprache überschreibt; per-Token-Fallback (roher Token) bleibt an der
// Verwendungsstelle (FORMAT_LABELS[x] || x).
// ---------------------------------------------------------------------------
const FORMAT_LABELS = Object.assign(
  {},
  (I18N["de"] && I18N["de"].formatLabels) || {},
  (I18N[lang] && I18N[lang].formatLabels) || {}
);

// Letzter nicht-leerer Suchbegriff (für Desktop-"Letzte Suche"-Feature)
var _lastQuery = "";

// q=-Parameter SOFORT lesen, bevor InstantSearch-Router die URL überschreibt
// (PHP-Resolver trägt den Suchbegriff als ?q= ein; Router entfernt unbekannte Params)
var _initialUrlQuery = new URLSearchParams(window.location.search).get("q") || "";

// Sort-Index-Namen
const SORT_ARTIST_ASC  = `${INDEX_NAME}/sort/artist_sort:asc`;
const SORT_PREIS_ASC   = `${INDEX_NAME}/sort/${PREIS_FELD}:asc`;
const SORT_PREIS_DESC  = `${INDEX_NAME}/sort/${PREIS_FELD}:desc`;

// ---------------------------------------------------------------------------
// Stop-word filter
// ---------------------------------------------------------------------------
function queryWithoutStopWords(query) {
  const words = query.replace(/[&/\\#,+()$~%.':*?<>{}]/g, "").split(" ");
  return words
    .map((word) => (STOP_WORDS.includes(word.toLowerCase()) ? null : word))
    .filter((w) => w)
    .join(" ")
    .trim();
}

// ---------------------------------------------------------------------------
// Typesense adapter
// ---------------------------------------------------------------------------
const typesenseInstantsearchAdapter = new TypesenseInstantSearchAdapter({
  server: TYPESENSE_SERVER_CONFIG,
  additionalSearchParameters: {
    // track_text mit Gewicht 1 - enthält Tracklisten die NICHT in search_blob sind
    // styles/genres mit Gewicht 2: ein Treffer auf den Stil ("Disco") oder das Genre
    // erscheint mit, rankt aber unter Titel-/Interpret-Treffern. genres steckt zwar
    // schon im search_blob, hier explizit für saubere Gewichtung + exakte Token-Treffer.
    // sku mit Gewicht 6: die Artikelnummer ("s51432", "c1938", "29057") ist direkt
    // eingebbar und steht als exakter Treffer ganz oben. Normale Wortsuchen bleiben
    // unverändert – Wörter treffen keine SKU-Token, es kommen höchstens hinten
    // zusätzliche Zahlen-Treffer dazu.
    query_by: "sku,title,artist,track_text,search_blob,styles,genres",
    query_by_weights: "6,4,3,1,1,2,2",
    include_fields:
      "sku,title,artist,artist_slug,artist_has_description,preis,preis_chf,image_path,thumb_280,thumb_400,thumb_w,thumb_h,format_group,cover_grade_code,vinyl_grade_code,genres,group_slug",
    // Nur sichtbare, vollständige Produkte anzeigen – globaler Default-Filter (+ Sichtbarkeit geteilter Markt)
    filter_by: BASIS_FILTER,
  },
  collectionSpecificSearchParameters: {
    [SORT_ARTIST_ASC]:  { sort_by: "artist_sort:asc" },
    [SORT_PREIS_ASC]:   { sort_by: `${PREIS_FELD}:asc` },
    [SORT_PREIS_DESC]:  { sort_by: `${PREIS_FELD}:desc` },
  },
});

const searchClient = typesenseInstantsearchAdapter.searchClient;

// ---------------------------------------------------------------------------
// InstantSearch setup
// ---------------------------------------------------------------------------
const search = instantsearch({
  searchClient,
  indexName: INDEX_NAME,
  routing: {
    router: history({ cleanUrlOnDispose: true }),
  },
  onStateChange({ uiState, setUiState }) {
    const state = uiState[INDEX_NAME] || {};
    const hasQuery = (state.query || "").trim() !== "";
    const hasRefinements =
      Object.keys(state.refinementList || {}).length > 0 ||
      Object.keys(state.numericMenu || {}).length > 0;
    const isMobile = window.matchMedia("(max-width: 991px)").matches;

    if (hasQuery || hasRefinements) {
      $("#hits-wrapper").removeClass("d-none");
      $("#browse-placeholder").addClass("d-none");
      // Mobile: Filter-Drawer beim Tippen schließen
      if (isMobile) {
        var fs = document.getElementById("filters-section");
        if (fs && fs.classList.contains("show")) {
          fs.classList.remove("show");
        }
      }
    } else {
      // Leere Suche → alle Artikel anzeigen (Typesense liefert bei leerem Query alle Docs)
      $("#hits-wrapper").removeClass("d-none");
      $("#browse-placeholder").addClass("d-none");
    }

    setUiState(uiState);
  },
  future: {
    preserveSharedStateOnUnmount: true,
  },
});

// ---------------------------------------------------------------------------
// Widgets
// ---------------------------------------------------------------------------
search.addWidgets([
  searchBox({
    container: "#searchbox",
    showSubmit: false,
    showReset: false,
    placeholder: t("searchPlaceholder"),
    autofocus: true,
    cssClasses: {
      input: "form-control searchbox-input",
    },
    queryHook(query, doSearch) {
      const modifiedQuery = queryWithoutStopWords(query);
      if (modifiedQuery.trim() !== "") {
        _lastQuery = modifiedQuery;
        // Shopware-Parent über neue Suchanfrage informieren (cross-origin sessionStorage-Bridge)
        if (window.parent !== window) {
          try {
            window.parent.postMessage(
              { type: "queryChanged", version: "v1", data: { query: modifiedQuery } },
              _shopwareOrigin || "*"
            );
          } catch (_) {}
        }
        doSearch(modifiedQuery);
      } else {
        // Desktop: leere Suche → letzte Suche weiter anzeigen
        const onDesktop = window.matchMedia("(min-width: 992px)").matches;
        if (onDesktop && _lastQuery) {
          doSearch(_lastQuery);
        } else {
          doSearch("");
        }
      }
    },
  }),

  stats({
    container: "#stats",
    templates: {
      text: ({ nbHits, hasNoResults, hasOneResult, processingTimeMS }) => {
        let statsText;
        if (hasNoResults) statsText = t("statsNoResults");
        else if (hasOneResult) statsText = t("statsOneResult");
        else statsText = t("statsManyResults").replace("{n}", FORMAT.zahl(nbHits));
        return `${statsText} ${t("statsTiming").replace("{ms}", processingTimeMS)}`;
      },
    },
  }),

  // Aktive Filter-Tags
  currentRefinements({
    container: "#current-refinements",
    cssClasses: {
      list: "list-unstyled d-flex flex-wrap gap-1 mb-0",
      item: "d-inline-flex align-items-center",
      label: "visually-hidden",
      category: "badge bg-primary d-flex align-items-center gap-1 px-2 py-1",
      delete: "btn-close btn-close-white ms-1",
    },
    transformItems(items) {
      const labelMap = {
        format_group:     t("refinementFormat"),
        cover_grade_code: t("refinementCover"),
        vinyl_grade_code: t("refinementVinyl"),
        genres:           t("refinementGenre"),
        styles:           t("refinementStyle"),
        preis:            t("refinementPrice"),
        preis_chf:        t("refinementPrice"),
      };
      return items.map((item) => ({
        ...item,
        label: labelMap[item.attribute] || item.attribute,
      }));
    },
  }),

  // Format
  refinementList({
    container: "#format-refinement-list",
    attribute: "format_group",
    sortBy: ["count:desc", "name:asc"],
    showMore: true,
    showMoreLimit: 20,
    templates: {
      showMoreText: ({ isShowingMore }) =>
        isShowingMore ? t("showLess") : t("showMore"),
    },
    cssClasses: {
      list: "list-unstyled mb-0",
      item: "mb-1",
      label: "d-flex align-items-center gap-2 filter-label",
      checkbox: "form-check-input flex-shrink-0",
      count: "badge bg-dark ms-auto",
      showMore: "btn btn-link btn-sm p-0 mt-1 text-decoration-none",
    },
    transformItems(items) {
      return items.map((item) => ({
        ...item,
        label: FORMAT_LABELS[item.label] || item.label,
        highlighted: FORMAT_LABELS[item.highlighted] || item.highlighted,
      }));
    },
  }),

  // Cover-Zustand
  refinementList({
    container: "#cover-grade-refinement-list",
    attribute: "cover_grade_code",
    sortBy: ["name:asc"],
    cssClasses: {
      list: "list-unstyled mb-0",
      item: "mb-1",
      label: "d-flex align-items-center gap-2 filter-label",
      checkbox: "form-check-input flex-shrink-0",
      count: "badge bg-dark ms-auto",
    },
  }),

  // Vinyl-Zustand
  refinementList({
    container: "#vinyl-grade-refinement-list",
    attribute: "vinyl_grade_code",
    sortBy: ["name:asc"],
    cssClasses: {
      list: "list-unstyled mb-0",
      item: "mb-1",
      label: "d-flex align-items-center gap-2 filter-label",
      checkbox: "form-check-input flex-shrink-0",
      count: "badge bg-dark ms-auto",
    },
  }),

  // Genre
  refinementList({
    container: "#genre-refinement-list",
    attribute: "genres",
    sortBy: ["count:desc", "name:asc"],
    showMore: true,
    showMoreLimit: 30,
    templates: {
      showMoreText: ({ isShowingMore }) =>
        isShowingMore ? t("showLess") : t("showMore"),
    },
    cssClasses: {
      list: "list-unstyled mb-0",
      item: "mb-1",
      label: "d-flex align-items-center gap-2 filter-label",
      checkbox: "form-check-input flex-shrink-0",
      count: "badge bg-dark ms-auto",
      showMore: "btn btn-link btn-sm p-0 mt-1 text-decoration-none",
    },
  }),

  // Stil – sofort feuernde Checkbox-Liste wie Genre (KEIN searchable/Lupe);
  // 300+ Werte über limit + showMore gelöst.
  refinementList({
    container: "#style-refinement-list",
    attribute: "styles",
    sortBy: ["count:desc", "name:asc"],
    limit: 12,
    showMore: true,
    showMoreLimit: 40,
    templates: {
      showMoreText: ({ isShowingMore }) =>
        isShowingMore ? t("showLess") : t("showMore"),
    },
    cssClasses: {
      list: "list-unstyled mb-0",
      item: "mb-1",
      label: "d-flex align-items-center gap-2 filter-label",
      checkbox: "form-check-input flex-shrink-0",
      count: "badge bg-dark ms-auto",
      showMore: "btn btn-link btn-sm p-0 mt-1 text-decoration-none",
    },
  }),

  // Preis-Filter (numericMenu, da preis kein Facet-Feld)
  numericMenu({
    container: "#price-selector",
    attribute: PREIS_FELD,
    items: [
      { label: t("filterPriceAll") },
      ...PREISBAENDER.map(([von, bis]) => Object.assign({ label: bandLabel(von, bis) }, von !== null ? { start: von } : {}, bis !== null ? { end: bis } : {})),
    ],
    cssClasses: {
      list: "list-unstyled mb-0",
      item: "mb-1",
      label: "d-flex align-items-center gap-2 filter-label",
      radio: "form-check-input flex-shrink-0",
    },
  }),

  // Sortierung
  sortBy({
    container: "#sort-by",
    items: [
      { label: t("sortRelevance"),  value: INDEX_NAME },
      { label: t("sortArtistAsc"), value: SORT_ARTIST_ASC },
      { label: t("sortPriceAsc"),  value: SORT_PREIS_ASC },
      { label: t("sortPriceDesc"), value: SORT_PREIS_DESC },
    ],
    cssClasses: {
      select: "form-select form-select-sm",
    },
  }),

  // Treffer
  infiniteHits({
    container: "#hits",
    cssClasses: {
      list: "list-unstyled grid-container",
      item: "d-flex flex-column search-result-card bg-white p-3",
      loadMore: "btn btn-outline-primary mx-auto d-block mt-4",
    },
    templates: {
      item: (hit, { html, components }) => html`
        <div class="d-flex flex-column gap-2">

          <!-- Produktbild: volle Breite, proportionale Höhe.
               Adressen kommen relativ aus dem Index -> bildQuellen() setzt die
               Shop-Herkunft davor und waehlt die skalierte Stufe (P1). -->
          ${(() => {
            const bild = bildQuellen(hit);
            return bild.src
              ? html`<img
                  src="${bild.src}"
                  srcset="${bild.srcset}"
                  sizes="(min-width: 992px) 260px, (min-width: 576px) 33vw, 45vw"
                  width="${hit.thumb_w || ""}"
                  height="${hit.thumb_h || ""}"
                  alt="${hit.title || ""}"
                  decoding="async"
                  loading="lazy"
                  class="hit-img-clickable"
                  data-pos="${hit.__position || ""}"
                  data-sku="${hit.sku || ""}"
                  data-title="${(hit.title || "").replace(/"/g, "&quot;")}"
                  data-group-slug="${hit.group_slug || ""}"
                  style="width:100%;height:auto;display:block;border-radius:6px;"
                />`
              : html`<div class="img-placeholder" style="width:100%;aspect-ratio:1/1;background:#e9ecef;border-radius:6px;"></div>`;
          })()}

          <!-- Info -->
          <div>
            <h6 class="fw-semibold mb-1 hit-title">
              ${components.Highlight({ hit, attribute: "title" })}
            </h6>

            <div class="mb-1" style="font-size:.82rem;font-weight:600;color:#2a1d18;">
              ${hit.artist_has_description && hit.artist_slug
                ? html`<a href="${_shopwareOrigin}/artist/${hit.artist_slug}"
                           target="_top"
                           style="color:#00728c;text-decoration:none;"
                           title="${(hit.artist || "").replace(/"/g, "&quot;")} Schallplatten kaufen">
                           ${components.Highlight({ hit, attribute: "artist" })}
                         </a>`
                : components.Highlight({ hit, attribute: "artist" })
              }
            </div>

            <!-- Badges -->
            <div class="mb-2 d-flex flex-wrap gap-1">
              ${hit.format_group
                ? html`<span class="hit-badge">${FORMAT_LABELS[hit.format_group] || hit.format_group}</span>`
                : ""}
              ${(hit.genres || []).slice(0, 2).map(
                (g) => html`<span class="hit-badge">${g}</span>`
              )}
              ${hit.cover_grade_code
                ? html`<span class="hit-badge" title="${t("refinementCover")}"><span aria-hidden="true">C: </span><abbr title="${GRADE_LABELS[hit.cover_grade_code] ?? hit.cover_grade_code}">${hit.cover_grade_code}</abbr></span>`
                : ""}
              ${hit.vinyl_grade_code
                ? html`<span class="hit-badge" title="${t("refinementVinyl")}"><span aria-hidden="true">V: </span><abbr title="${GRADE_LABELS[hit.vinyl_grade_code] ?? hit.vinyl_grade_code}">${hit.vinyl_grade_code}</abbr></span>`
                : ""}
            </div>

            <div class="small text-muted mb-2">
              <span class="me-2"><span aria-hidden="true">${t("fieldSku")}: </span>${hit.sku || ""}</span>
              ${hit[PREIS_FELD] != null
                ? html`<strong>${FORMAT.preis(hit[PREIS_FELD], WAEHRUNG_ZEICHEN)}</strong>
                  <span class="d-block" style="font-size:.7rem;color:rgba(42,29,24,.5);">${PREIS_FELD === "preis" ? t("hitPriceVat") : t("hitPriceShipping")}</span>`
                : ""}
            </div>

            <button
              class="btn btn-sm btn-primary w-100 js-select"
              data-pos="${hit.__position || ""}"
              data-sku="${hit.sku || ""}"
              data-title="${(hit.title || "").replace(/"/g, "&quot;")}"
              data-group-slug="${hit.group_slug || ""}"
              aria-label="${(hit.title || "").replace(/"/g, "&quot;")} – ${t("selectButton")}"
            >
              <span aria-hidden="true">${t("selectButton")}</span>
            </button>
          </div>

        </div>
      `,
      empty: (data, { html }) =>
        html`<p class="text-muted">${t("noResultsText")
          .replace("{q}", data.query)}</p>`,
    },
  }),

  configure({
    hitsPerPage: 16,
  }),
]);

// ---------------------------------------------------------------------------
// Event handlers
// ---------------------------------------------------------------------------

// Shared navigation function: navigate top-level Shopware window to product page.
// Works cross-origin without requiring the Shopware plugin JS to be compiled.
function _doSelect(sku, title, groupSlug) {
  // Aktuelle Suche merken, damit "Zurück" sie wiederherstellt
  if (_lastQuery) {
    sessionStorage.setItem("ts_return_query", _lastQuery);
  }

  // Step 7: trägt der Treffer ein group_slug (7a, im Typesense-Doc), tief auf die Gruppen-PDP
  // /article/{group_slug}#{sku} verlinken (Anker == nackte SKU, von Google entfernt → SEO-silent;
  // die Empfangsseite committet die Zeile per location.hash). Ohne group_slug → klassischer Resolver.
  var target = groupSlug
    ? "/article/" + groupSlug + "#" + sku
    : "/typesense/resolve/" + encodeURIComponent(sku);

  if (_shopwareOrigin && window.top && window.top !== window) {
    // Suchwege 04.10.2026: 150 ms Vorlauf, damit die hitClick-Meldung (_meldeKlick) beim Shop ankommt.
    setTimeout(function () { window.top.location.href = _shopwareOrigin + target; }, 150);
  } else if (window.parent && window.parent !== window) {
    window.parent.postMessage(
      { type: "select", version: "v1", ts: Date.now(), data: { sku, title, groupSlug } },
      _shopwareOrigin || "*"
    );
  } else {
    console.log("Selected:", { sku, title, groupSlug });
    alert(`${sku} – ${title}`);
  }
}

// Inject hover-zoom CSS once (desktop only via media query; mobile has no hover)
(function () {
  var style = document.createElement("style");
  style.textContent = [
    ".hit-img-clickable { cursor: pointer; }",
    "@media (min-width: 768px) {",
    "  .hit-img-clickable { transition: transform 0.2s ease, box-shadow 0.2s ease; }",
    "  .hit-img-clickable:hover { transform: scale(2.0); box-shadow: 0 8px 24px rgba(0,0,0,0.4); z-index: 100; position: relative; }",
    "}",
  ].join("\n");
  document.head.appendChild(style);
}());

// ---------------------------------------------------------------------------
// Suchmessung (Vinyliebhaberei.de N80, 04.10.2026): der Shop (layout/meta.html.twig, N31-Melder)
// erfaehrt sonst weder die Trefferzahl noch, welcher Treffer geklickt wurde - der Iframe ist eine
// fremde Herkunft. Zwei Nachrichten an den Parent:
//   {type:"results",  version:"v1", data:{query, found}}        nach jeder Suche (gleiche Suche nur einmal)
//   {type:"hitClick", version:"v1", data:{query, sku, pos, art}} beim Klick auf einen Treffer (pos ab 1)
// Der Shop meldet daraus Matomo-Suche "Ergebnisseite" mit Trefferzahl bzw. Ereignis "Treffer geklickt".
// ---------------------------------------------------------------------------
function _meldeKlick(el, art) {
  if (window.parent === window) return;
  var pos = parseInt(el.getAttribute("data-pos"), 10);
  if (isNaN(pos)) {
    var li = el.closest("li");
    var alle = li ? Array.prototype.slice.call(document.querySelectorAll("#hits li")) : [];
    pos = li ? alle.indexOf(li) + 1 : 0;
  }
  try {
    window.parent.postMessage(
      { type: "hitClick", version: "v1",
        data: { query: _lastQuery || "", sku: el.getAttribute("data-sku") || "", pos: pos, art: art } },
      _shopwareOrigin || "*"
    );
  } catch (_) {}
}

var _letzteTrefferMeldung = "";
search.on("render", function () {
  if (window.parent === window) return;
  var r = search.helper && search.helper.lastResults;
  if (!r) return;
  var q = String(r.query || "").trim();
  var key = q + "|" + r.nbHits;
  if (!q || key === _letzteTrefferMeldung) return;
  _letzteTrefferMeldung = key;
  try {
    window.parent.postMessage(
      { type: "results", version: "v1", data: { query: q, found: r.nbHits } },
      _shopwareOrigin || "*"
    );
  } catch (_) {}
});

search.on("render", function () {
  // Kuenstler-Link im Treffer (target=_top) als Klick melden
  $("#hits a[href*='/artist/']")
    .off("click.vlmeldung")
    .on("click.vlmeldung", function () {
      var li = this.closest("li");
      var knopf = li ? li.querySelector(".js-select") : null;
      _meldeKlick(knopf || this, "kuenstler");
    });

  // "Auswählen" button
  $("#hits .js-select")
    .off("click")
    .on("click", (e) => {
      const sku       = e.currentTarget.getAttribute("data-sku");
      const title     = e.currentTarget.getAttribute("data-title");
      const groupSlug = e.currentTarget.getAttribute("data-group-slug");
      _meldeKlick(e.currentTarget, "artikel");
      _doSelect(sku, title, groupSlug);
    });

  // Image click = same as Auswählen
  $("#hits .hit-img-clickable")
    .off("click")
    .on("click", (e) => {
      const sku       = e.currentTarget.getAttribute("data-sku");
      const title     = e.currentTarget.getAttribute("data-title");
      const groupSlug = e.currentTarget.getAttribute("data-group-slug");
      _meldeKlick(e.currentTarget, "artikel");
      _doSelect(sku, title, groupSlug);
    });

  // Dynamically set transform-origin so zoomed image never overflows iframe edge:
  // images in the right half of the viewport expand to the left, left half to the right.
  // Only active on desktop (matchMedia guard keeps mobile clean).
  if (window.matchMedia("(min-width: 768px)").matches) {
    $("#hits .hit-img-clickable")
      .off("mouseover.zoom")
      .on("mouseover.zoom", function () {
        var rect = this.getBoundingClientRect();
        var viewportWidth = document.documentElement.clientWidth;
        var imgCenterX = rect.left + rect.width / 2;
        var xOrigin = imgCenterX > viewportWidth / 2 ? "right" : "left";
        this.style.transformOrigin = xOrigin + " top";
      });
  }
});

search.start();

// ---------------------------------------------------------------------------
// Dynamic iframe height: send body.scrollHeight to parent on every resize.
// The Twig inline script listens for these messages and sets iframe.style.height.
// ---------------------------------------------------------------------------
if (window.parent !== window && typeof ResizeObserver !== "undefined") {
  var _resizeTimer;
  var _resizeObserver = new ResizeObserver(function () {
    clearTimeout(_resizeTimer);
    _resizeTimer = setTimeout(function () {
      var height = document.body.scrollHeight;
      window.parent.postMessage(
        { type: "resize", version: "v1", data: { height: height } },
        _shopwareOrigin || "*"
      );
    }, 80);
  });
  _resizeObserver.observe(document.body);
}

// ---------------------------------------------------------------------------
// Initial render: show hits-wrapper if page was opened with a query in the URL
// (onStateChange is NOT called for the initial URL-restored state)
//
// Compatibility: Shopware Twig may encode query as {collectionName}[query]=...
// using a different collection name than INDEX_NAME (happens when PHP resolver
// hasn't been updated yet). We detect any *[query] param as fallback.
// ---------------------------------------------------------------------------
(function () {
  var _firstRenderDone = false;
  search.on("render", function onFirstRender() {
    if (_firstRenderDone) return;
    _firstRenderDone = true;

    // Prüfen ob der User von einer Produktseite zurücknavigiert ist.
    // Dann hat die gespeicherte SPA-Suche Vorrang vor dem URL-Parameter
    // (der enthält noch die ursprüngliche Shopware-Suchanfrage, nicht die
    // zuletzt im SPA eingegebene Suche).
    var returnQuery = sessionStorage.getItem("ts_return_query");
    if (returnQuery) {
      sessionStorage.removeItem("ts_return_query");
      _lastQuery = returnQuery;
      search.setUiState(function (prev) {
        return Object.assign({}, prev, {
          [INDEX_NAME]: Object.assign({}, prev[INDEX_NAME] || {}, { query: returnQuery }),
        });
      });
      $("#hits-wrapper").removeClass("d-none");
      $("#browse-placeholder").addClass("d-none");
      return;
    }

    var urlParams = new URLSearchParams(window.location.search);

    // Primary: correct collection name
    var initialQuery = urlParams.get(INDEX_NAME + "[query]") || "";

    // Fallback: any {something}[query] param (e.g. old Twig with wrong collection name)
    if (!initialQuery) {
      urlParams.forEach(function (value, key) {
        if (!initialQuery && /\[query\]$/.test(key) && value.trim()) {
          initialQuery = value.trim();
        }
      });
    }

    // Fallback: plain ?q= param (captured before InstantSearch router could overwrite URL)
    if (!initialQuery) {
      initialQuery = _initialUrlQuery;
    }

    if (initialQuery) {
      _lastQuery = initialQuery;
      // Shopware-Parent sofort informieren → sessionStorage für "Zurück"-Bar setzen
      if (window.parent !== window) {
        try {
          window.parent.postMessage(
            { type: "queryChanged", version: "v1", data: { query: initialQuery } },
            _shopwareOrigin || "*"
          );
        } catch (_) {}
      }
      // Trigger search with the found query (also populates the searchBox widget)
      search.setUiState(function (prev) {
        return Object.assign({}, prev, {
          [INDEX_NAME]: Object.assign({}, prev[INDEX_NAME] || {}, { query: initialQuery }),
        });
      });
    }
    // Immer Artikel anzeigen – bei leerem Query zeigt Typesense alle Docs
    $("#hits-wrapper").removeClass("d-none");
    $("#browse-placeholder").addClass("d-none");
  });
}());

// ---------------------------------------------------------------------------
// postMessage listener: Shopware → SPA
// Payload: { type:"search", version:"v1", ts, data:{query} }
// Sent by Shopware after iframe load when user came from the header search
// ---------------------------------------------------------------------------
(function () {
  var allowedOrigin = _shopwareOrigin;

  window.addEventListener("message", function (e) {
    if (allowedOrigin && e.origin !== allowedOrigin) return;
    var msg = e.data;
    if (!msg || msg.type !== "search" || msg.version !== "v1") return;
    var query = msg.data ? String(msg.data.query || "").trim() : "";

    // Immer Artikel anzeigen – leere Query liefert alle Docs
    $("#hits-wrapper").removeClass("d-none");
    $("#browse-placeholder").addClass("d-none");

    search.setUiState(function (prev) {
      return Object.assign({}, prev, {
        [INDEX_NAME]: Object.assign({}, prev[INDEX_NAME] || {}, { query: query }),
      });
    });
  });
}());

$(function () {
  // Seite mit Sprachstring befüllen
  document.title = t("pageTitle");
  $("#browse-placeholder p").text(t("browsePlaceholder"));
  // Filter-Ueberschriften stehen in index.html fest deutsch; je Sprache aus i18n.json (Sprachen/Englisch v0.42.7, C8-Nachlauf F8).
  // Zuordnung ueber den Container darunter, nicht ueber die Reihenfolge der Ueberschriften.
  [["format-refinement-list", "filterFormat"], ["cover-grade-refinement-list", "filterCoverGrade"],
   ["vinyl-grade-refinement-list", "filterVinylGrade"], ["genre-refinement-list", "filterGenre"],
   ["style-refinement-list", "filterStyle"], ["price-selector", "filterPrice"]].forEach(function (z) {
    var el = document.getElementById(z[0]);
    var h = el && el.previousElementSibling;
    if (h && h.classList.contains("filter-heading")) h.textContent = t(z[1]);
  });
});

// ---------------------------------------------------------------------------
// postMessage: Shopware → SPA – Filter-Toggle
// Payload: { type:"toggleFilter", version:"v1" }
// Shopware sendet dies vom mobilen "Filter"-FAB-Button.
// SPA toggelt die Bootstrap-Collapse-Sektion und scrollt – falls geöffnet –
// das Parent-Window an den Iframe-Anfang.
// ---------------------------------------------------------------------------
(function () {
  var allowedOrigin = _shopwareOrigin;

  window.addEventListener("message", function (e) {
    if (allowedOrigin && e.origin !== allowedOrigin) return;
    var msg = e.data;
    if (!msg || msg.type !== "toggleFilter" || msg.version !== "v1") return;

    var fs = document.getElementById("filters-section");
    if (!fs) return;

    var isOpen = fs.classList.contains("show");
    if (isOpen) {
      fs.classList.remove("show");
    } else {
      fs.classList.add("show");
    }

    // Wenn der Filter eingeblendet wird → Parent nach oben scrollen
    if (!isOpen && window.parent !== window) {
      try {
        window.parent.postMessage(
          { type: "scrollToIframeTop", version: "v1" },
          _shopwareOrigin || "*"
        );
      } catch (_) {}
    }
  });
}());
